import { Toaster as Sonner } from '@/components/ui/sonner';
import { Toaster } from '@/components/ui/toaster';
import { TooltipProvider } from '@/components/ui/tooltip';
import { toast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { identifyClarityUser } from '@/lib/clarity';
import { GOOGLE_AUTH_CREDIT_CHECK_KEY, getUserCreditBalance } from '@/lib/homepageAuthRedirect';
import { MARKETPLACE_CATEGORY_SLUGS } from '@/lib/marketplaceNavigation';
import { syncUrlParamsToSessionStorage } from '@/lib/tracking';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
//import { SpeedInsights } from '@vercel/speed-insights/react';
import posthog from 'posthog-js';
import { Suspense, useEffect } from 'react';
import { lazyWithRetry } from '@/lib/lazyWithRetry';
import { ErrorBoundary } from './components/shared/ErrorBoundary';
import { Helmet } from 'react-helmet-async';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { AdminGuard } from './components/auth/AdminGuard';
import { AuthGuard } from './components/auth/AuthGuard';
import { CookieBanner } from './components/CookieBanner';
import { Footer } from './components/layout/Footer';
import { Navbar } from './components/layout/Navbar';
import { SmallCheckoutNavbar } from './components/layout/SmallCheckoutNavbar';
import { SmallFooter } from './components/layout/SmallFooter';
import { BrevoLoader } from './components/shared/BrevoLoader';
import { UtmCookieManager } from './components/shared/UtmCookieManager';
import { VisitorLifetimeOffer } from './components/marketing/VisitorLifetimeOffer';
import ScrollToTop from './scrollToTop';

const AddCredits = lazyWithRetry(() => import('./pages/AddCredits'));
const AuthorGrowthChallenge = lazyWithRetry(() => import('./pages/AuthorGrowthChallenge'));
const AddCreditsFlashSale = lazyWithRetry(() => import('./pages/AddCreditsFlashSale'));
const AcceptableUsePolicy = lazyWithRetry(() => import('./pages/AcceptableUsePolicy'));
const AddCreditsMothersDay = lazyWithRetry(() => import('./pages/AddCreditsMothersDay'));
const AddCreditsOfferEnded = lazyWithRetry(() => import('./pages/AddCreditsOfferEnded'));
const BookIdeas = lazyWithRetry(() => import('./pages/BookIdeas'));
const BookIdeasResult = lazyWithRetry(() => import('./pages/BookIdeasResult'));
const BookGrowth = lazyWithRetry(() => import('./pages/BookGrowth'));
const GrowthAutopilot = lazyWithRetry(() => import('./pages/GrowthAutopilot'));
const GrowthAutopilotDemo = lazyWithRetry(() => import('./pages/GrowthAutopilotDemo'));
const GrowthAutopilotOnboarding = lazyWithRetry(() => import('./pages/GrowthAutopilotOnboarding'));
const GrowthAutopilotContact = lazyWithRetry(() => import('./pages/GrowthAutopilotContact'));
const SelfPublisherPlaybooks = lazyWithRetry(() => import('./pages/SelfPublisherPlaybooks'));
const LifetimeTestOct2025 = lazyWithRetry(() => import('./pages/LifetimeTestOct2025'));
const LifetimeCheckoutOct2025 = lazyWithRetry(() => import('./pages/LifetimeCheckoutOct2025'));
const LifetimeUpgradeOfferOct2025 = lazyWithRetry(() => import('./pages/LifetimeUpgradeOfferOct2025'));
const LifetimeCreditsOfferOct2025 = lazyWithRetry(() => import('./pages/LifetimeCreditsOfferOct2025'));
const LifetimeSuccessOct2025 = lazyWithRetry(() => import('./pages/LifetimeSuccessOct2025'));
const AdminDashboard = lazyWithRetry(() => import('./pages/AdminDashboard'));
const AffiliatePage = lazyWithRetry(() => import('./pages/AffiliatePage'));
const AffiliateChallenge = lazyWithRetry(() => import('./pages/AffiliateChallenge.tsx'));
const AffiliateChallengeLeaderboard = lazyWithRetry(() => import('./pages/AffiliateChallengeLeaderboard.tsx'));
const AffiliateChallengeResources = lazyWithRetry(() => import('./pages/AffiliateChallengeResources.tsx'));
const AffiliateRedirect = lazyWithRetry(() => import('./pages/AffiliateRedirect'));
const Superscale = lazyWithRetry(() => import('./pages/Superscale'));
const EnterpriseContact = lazyWithRetry(() => import('./pages/EnterpriseContact.tsx'));
const AuthorAcceleratorPage = lazyWithRetry(() => import('./pages/AuthorAcceleratorPage'));
const AuthorAcceleratorVideoPage = lazyWithRetry(() => import('./pages/AuthorAcceleratorVideoPage'));
const MarketplaceContact = lazyWithRetry(() => import('./pages/MarketplaceContact'));
const AuthorDashboard = lazyWithRetry(() => import('./pages/AuthorDashboard'));
const AuthorPage = lazyWithRetry(() => import('./pages/AuthorPage'));
const AuthorsPage = lazyWithRetry(() => import('./pages/AuthorsPage'));
const Blog = lazyWithRetry(() => import('./pages/Blog'));
const BlogPost = lazyWithRetry(() => import('./pages/BlogPost'));
const BookMarketing = lazyWithRetry(() => import('./pages/BookMarketing'));
const NativeAlternatives = lazyWithRetry(() => import('./pages/NativeAlternatives'));
const BooksteraVsNative = lazyWithRetry(() => import('./pages/BooksteraVsNative'));
const SeoContentPage = lazyWithRetry(() => import('./pages/SeoContentPage'));
const CompareHub = lazyWithRetry(() => import('./pages/Compare'));
const DedicatedComparisonPage = lazyWithRetry(() => import('./pages/Compare').then((module) => ({ default: module.DedicatedComparisonPage })));
const BookDetails = lazyWithRetry(() => import('./pages/BookDetails'));
const Bundles = lazyWithRetry(() => import('./pages/Bundles'));
const MarketplaceBundleDetails = lazyWithRetry(() => import('./pages/MarketplaceBundleDetails'));
const BooksteraOMatic = lazyWithRetry(() => import('./pages/BooksteraOMatic'));
const Bookster = lazyWithRetry(() => import('./pages/Bookster'));
const BookPreview = lazyWithRetry(() => import('./pages/BookPreview'));
const BusinessModel = lazyWithRetry(() => import('./pages/BusinessModel'));
const ChapterEdit = lazyWithRetry(() => import('./pages/ChapterEdit'));
const Coaching = lazyWithRetry(() => import('./pages/Coaching'));
const CoachingThankYou = lazyWithRetry(() => import('./pages/CoachingThankYou'));
const Contact = lazyWithRetry(() => import('./pages/Contact'));
const ContactThankYou = lazyWithRetry(() => import('./pages/ContactThankYou'));
const CreateBook = lazyWithRetry(() => import('./pages/CreateBook'));
const CodeOfConduct = lazyWithRetry(() => import('./pages/CodeOfConduct'));
const CreditsOfferPeakJanuary2026 = lazyWithRetry(() => import('./pages/CreditsOfferPeakJanuary2026'));
const DownloadPage = lazyWithRetry(() => import('./pages/DownloadPage'));
const EnterpriseSolutions = lazyWithRetry(() => import('./pages/EnterpriseSolutions'));
const Features = lazyWithRetry(() => import('./pages/Features'));
const Flexco = lazyWithRetry(() => import('./pages/Flexco'));
const FunnelPage = lazyWithRetry(() => import('./pages/FunnelPage'));
const Help = lazyWithRetry(() => import('./pages/Help'));
const Imprint = lazyWithRetry(() => import('./pages/Imprint'));
const Create = lazyWithRetry(() => import('./pages/Create'));
const Index = lazyWithRetry(() => import('./pages/Index'));
const LandingPage = lazyWithRetry(() => import('./pages/LandingPage'));
const RestoredLifetimeCheckout = lazyWithRetry(() => import('./pages/LifetimeCheckoutMediumOld.tsx'));
const LifetimeMay12Routes = lazyWithRetry(() => import('./restored-lifetime-may12/LifetimeMay12Routes'));
const LifetimeFunnel = lazyWithRetry(() => import('./pages/LifetimeFunnel.tsx'));
const LifetimeFunnelCheckoutPage = lazyWithRetry(() => import('./pages/LifetimeFunnelCheckoutPage.tsx'));
const LifetimeFunnelThankYouPage = lazyWithRetry(() => import('./pages/LifetimeFunnelThankYouPage.tsx'));
const LifetimeFunnelUpgradePage = lazyWithRetry(() => import('./pages/LifetimeFunnelUpgradePage.tsx'));
const LifetimeFunnelAcceleratorUpgradePage = lazyWithRetry(() => import('./pages/LifetimeFunnelAcceleratorUpgradePage.tsx'));
const LifetimeStarter = lazyWithRetry(() => import('./pages/LifetimeStarter'));
const RedesignedMarketplace = lazyWithRetry(() => import('./pages/marketplace/RedesignedMarketplace'));
const MyBooks = lazyWithRetry(() => import('./pages/MyBooks'));
const NotFoundPage = lazyWithRetry(() => import('./pages/NotFoundPage'));
const PAYGSuccess = lazyWithRetry(() => import('./pages/PAYGSuccess'));
const Partnerships = lazyWithRetry(() => import('./pages/Partnerships'));
const Partners = lazyWithRetry(() => import('./pages/Partners'));
const Press = lazyWithRetry(() => import('./pages/Press'));
const Pricing = lazyWithRetry(() => import('./pages/Pricing'));
const Reviews = lazyWithRetry(() => import('./pages/Reviews'));
const IsBooksteraLegit = lazyWithRetry(() => import('./pages/IsBooksteraLegit'));
const About = lazyWithRetry(() => import('./pages/About'));
const SuccessStories = lazyWithRetry(() => import('./pages/SuccessStories'));
const PrivacyPolicy = lazyWithRetry(() => import('./pages/PrivacyPolicy'));
const ProductRedirect = lazyWithRetry(() => import('./pages/ProductRedirect'));
const Profile = lazyWithRetry(() => import('./pages/Profile'));
const Pseudonyms = lazyWithRetry(() => import('./pages/Pseudonyms'));
const Purchases = lazyWithRetry(() => import('./pages/Purchases'));
const ResetPassword = lazyWithRetry(() => import('./pages/ResetPassword'));
const SignIn = lazyWithRetry(() => import('./pages/SignIn'));
const SignUp = lazyWithRetry(() => import('./pages/SignUp'));
const Subscribe = lazyWithRetry(() => import('./pages/Subscribe'));
const SubscriptionSuccess = lazyWithRetry(() => import('./pages/SubscriptionSuccess'));
const Success = lazyWithRetry(() => import('./pages/Success'));
const Support = lazyWithRetry(() => import('./pages/Support'));
const Survey = lazyWithRetry(() => import('./pages/Survey'));
const Terms = lazyWithRetry(() => import('./pages/Terms'));
const ThankYouPage = lazyWithRetry(() => import('./pages/ThankYouPage'));
const UnsubscribePage = lazyWithRetry(() => import('./pages/UnsubscribePage'));
const UseCasePage = lazyWithRetry(() => import('./pages/UseCasePage'));
const Upgrade = lazyWithRetry(() => import('./pages/Upgrade'));
const GrowthAutopilotOffer = lazyWithRetry(() => import('./pages/GrowthAutopilotOffer'));
const UpgradeYearly = lazyWithRetry(() => import('./pages/UpgradeYearly'));
const LifetimeUpgrade = lazyWithRetry(() => import('./pages/LifetimeUpgrade'));
const MothersDayBookIdeaGenerator = lazyWithRetry(() => import('./pages/MothersDayBookIdeaGenerator'));
const OrderbumpSuccess = lazyWithRetry(() => import('./pages/OrderbumpSuccess'));
const UpgradeCreator = lazyWithRetry(() => import('./pages/UpgradeCreator'));
const Welcome = lazyWithRetry(() => import('./pages/Welcome'));
const WaitlistPage = lazyWithRetry(() => import('./pages/WaitlistPage'));
const ToolsDirectoryPage = lazyWithRetry(() => import('./pages/tools/ToolsDirectoryPages').then((module) => ({ default: module.ToolsDirectoryPage })));
const ToolsCategoriesPage = lazyWithRetry(() => import('./pages/tools/ToolsDirectoryPages').then((module) => ({ default: module.ToolsCategoriesPage })));
const ToolCategoryPage = lazyWithRetry(() => import('./pages/tools/ToolsDirectoryPages').then((module) => ({ default: module.ToolCategoryPage })));
const ToolDetailPage = lazyWithRetry(() => import('./pages/tools/ToolsDirectoryPages').then((module) => ({ default: module.ToolDetailPage })));
const ToolComparisonPage = lazyWithRetry(() => import('./pages/tools/ToolsDirectoryPages').then((module) => ({ default: module.ToolComparisonPage })));
const ToolsFreePage = lazyWithRetry(() => import('./pages/tools/ToolsDirectoryPages').then((module) => ({ default: module.ToolsFreePage })));
const ToolsSubmitPage = lazyWithRetry(() => import('./pages/tools/ToolsDirectoryPages').then((module) => ({ default: module.ToolsSubmitPage })));
const ToolsMethodologyPage = lazyWithRetry(() => import('./pages/tools/ToolsDirectoryPages').then((module) => ({ default: module.ToolsMethodologyPage })));

const queryClient = new QueryClient();

function UrlParamStorageSync() {
  const location = useLocation();

  useEffect(() => {
    syncUrlParamsToSessionStorage();
  }, [location.pathname, location.search]);

  return null;
}

function PreserveSearchRedirect({ to }: { to: string }) {
  const { search, hash } = useLocation();
  return <Navigate to={`${to}${search}${hash}`} replace />;
}

function RouteIndexingRules() {
  const { pathname } = useLocation();
  const privatePrefixes = ['/admin-dashboard', '/author-dashboard', '/my-books', '/profile', '/purchases', '/signin', '/signup', '/reset', '/book-preview', '/chapter/', '/create-book', '/checkout', '/thank-you', '/lifetime-starter'];
  if (!privatePrefixes.some((prefix) => pathname === prefix || pathname.startsWith(prefix))) return null;
  return <Helmet><meta name="robots" content="noindex,nofollow" /></Helmet>;
}

function RouteErrorFallback() {
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-4 px-6 py-24 text-center">
      <h1 className="text-2xl font-semibold text-slate-900">This page could not load</h1>
      <p className="max-w-md text-slate-600">
        Part of the page failed to load. Reload to try again.
      </p>
      <button
        type="button"
        onClick={() => window.location.reload()}
        className="rounded-lg bg-slate-900 px-5 py-2.5 font-semibold text-white transition hover:bg-slate-800"
      >
        Reload page
      </button>
    </div>
  );
}

function App() {
  useEffect(() => {
    const identifyUser = (authUser: any) => {
      if (!authUser) return;

      identifyClarityUser(authUser);
      posthog.identify(authUser.id, { email: authUser.email, created_at: authUser.created_at });

      if (typeof window !== 'undefined' && authUser.email) {
        const brevoQueue = (window as any).Brevo || ((window as any).Brevo = []);
        brevoQueue.push([
          'identify',
          {
            email: authUser.email,
          },
        ]);
      }
    };

    syncUrlParamsToSessionStorage();

    supabase.auth.getUser().then(({ data: { user } }) => {
      if (user) {
        identifyUser(user);
      }
    });

    // Check for authentication errors in URL hash
    const hash = window.location.hash;
    if (hash.includes('error=')) {
      const params = new URLSearchParams(hash.substring(1));
      const error = params.get('error');
      const errorCode = params.get('error_code');
      const errorDescription = params.get('error_description');
      const decodedDescription = errorDescription?.replace(/\+/g, ' ');
      const fullDescription = error === 'access_denied' || errorCode === 'otp_expired'
        ? (
          <div className="flex flex-col gap-1">
            <span>{decodedDescription || 'This sign-in link has expired or has already been used.'}</span>
            <span>
              Sign-in links can only be used once and expire after one hour. Request a new link and set your password at{' '}
              <a
                href="https://www.bookstera.com/reset"
                className="text-white underline font-semibold"
              >
                bookstera.com/reset
              </a>
              .
            </span>
          </div>
        )
        : decodedDescription || 'An error occurred during authentication';

      if (error) {
        toast({
          title: "Authentication Error",
          description: fullDescription,
          variant: "destructive",
          duration: Infinity, // Never disappears
        });
      }
    }

    // Listen for successful authentication (invite flow)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'SIGNED_OUT') posthog.reset();
      if (event === 'SIGNED_IN' && session?.user) {
        const changedUser = posthog.get_distinct_id() !== session.user.id;
        identifyUser(session.user);
        if (changedUser) posthog.capture('SIGNED_IN');
      }
      // INITIAL_SESSION, TOKEN_REFRESHED and USER_UPDATED are session plumbing,
      // not meaningful user actions. Supabase also repeats SIGNED_IN on refocus.
      if (session?.user && event === 'PASSWORD_RECOVERY') {
        posthog.capture(event);
      }

      if (event === 'SIGNED_IN' && session?.user) {
        // Check if this is from an invite (by checking if user was just created or has no password set)
        const isFromInvite = window.location.hash.includes('type=invite') ||
          window.location.hash.includes('type=recovery') ||
          session.user.app_metadata?.provider === 'email';

        if (isFromInvite && !localStorage.getItem('invite_welcome_shown')) {
          localStorage.setItem('invite_welcome_shown', 'true');
          toast({
            title: "Welcome to Bookstera!",
            description: (
              <div className="flex flex-col gap-2">
                <p>You are now successfully logged into your Bookstera account. If you didn't do so, yet, please set a password now.</p>
                <a href="/profile#account" className="text-primary underline font-medium">
                  Set password →
                </a>
              </div>
            ),
            duration: 10000,
          });

          // Clear the flag after a delay so it can show again if needed
          setTimeout(() => {
            localStorage.removeItem('invite_welcome_shown');
          }, 15000);
        }

        const shouldCheckGoogleAuthCredits = sessionStorage.getItem(
          GOOGLE_AUTH_CREDIT_CHECK_KEY
        );

        if (shouldCheckGoogleAuthCredits) {
          sessionStorage.removeItem(GOOGLE_AUTH_CREDIT_CHECK_KEY);

          try {
            const balance = await getUserCreditBalance(session.user);

            if (balance <= 0 && window.location.pathname !== '/pricing') {
              window.location.replace('/pricing');
            }
          } catch (error) {
            console.error('Error checking Google auth credits:', error);

            if (window.location.pathname !== '/pricing') {
              window.location.replace('/pricing');
            }
          }
        }
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <div className="min-h-screen bg-white flex flex-col">
          <Toaster />
          <Sonner />
          <CookieBanner />
          {/*<SpeedInsights />*/}
          <BrowserRouter>
            <BrevoLoader />
            <UrlParamStorageSync />
            <RouteIndexingRules />
            <ScrollToTop />
            {/*<FunnelBlueprintBanner />*/}
            <UtmCookieManager />
            <ErrorBoundary name="app-routes" fallback={<RouteErrorFallback />}>
              <Suspense fallback={null}>
                <Routes>
                <Route path="/lifetime-starter" element={<LifetimeStarter />} />
                <Route path="/challenge" element={<AuthorGrowthChallenge />} />
                <Route path="/challenge/start" element={<AuthorGrowthChallenge />} />
                <Route path="/challenge/offer" element={<AuthorGrowthChallenge />} />
                <Route path="/challenge/success" element={<AuthorGrowthChallenge />} />
                <Route path="/challenge/dashboard" element={<AuthorGrowthChallenge />} />
                {((import.meta as any).env?.DEV || (import.meta as any).env?.VITE_GROWTH_AUTOPILOT_DEMO === 'true') && <Route path="/growth-autopilot/demo" element={<GrowthAutopilotDemo />} />}
                <Route path="/growth-autopilot/onboarding" element={<GrowthAutopilotOnboarding />} />
                <Route path="/growth-autopilot/contact" element={<GrowthAutopilotContact />} />
                {/*<Route path="/flipbook/:bookId" element={<FlipbookViewer />} />*/}
                <Route path="/landing/:bookId" element={<LandingPage />} />

                <Route path="/landing/:bookId" element={<LandingPage />} />
                <Route path="/thank-you/:bookId" element={<ThankYouPage />} />
                <Route path="/download/:bookId" element={<DownloadPage />} />
                <Route
                  path="/unsubscribe/:bookId"
                  element={<UnsubscribePage />}
                />
                <Route path="/subscribe/:bookId" element={<Subscribe />} />
                <Route
                  path="/product-redirect/:productId"
                  element={<ProductRedirect />}
                />
                <Route
                  path="/affiliate-redirect"
                  element={<AffiliateRedirect />}
                />

                <Route path="/lifetime-checkout" element={<PreserveSearchRedirect to="/lt2/checkout" />} />
                <Route path="/lt3" element={<LifetimeTestOct2025 />} />
                <Route
                  path="/lt3/checkout"
                  element={
                    <div className="flex-1">
                      <SmallCheckoutNavbar />
                      <LifetimeCheckoutOct2025 />
                      <SmallFooter />
                    </div>
                  }
                />
                <Route path="/lt3/upgrade-offer" element={<LifetimeUpgradeOfferOct2025 />} />
                <Route path="/lt3/credits-offer" element={<LifetimeCreditsOfferOct2025 />} />
                <Route path="/lt3/success" element={<LifetimeSuccessOct2025 />} />
                <Route path="/lt-checkout" element={<PreserveSearchRedirect to="/lt2/checkout" />} />
                <Route path="/life-time-checkout" element={<PreserveSearchRedirect to="/lt2/checkout" />} />
                <Route path="/lifetime-checkout-iteration" element={<PreserveSearchRedirect to="/lt2/checkout" />} />
                <Route path="/lifetime-offer-checkout" element={<PreserveSearchRedirect to="/lt2/checkout" />} />
                <Route path="/starter-kit-checkout" element={<PreserveSearchRedirect to="/lt2/checkout" />} />
                <Route
                  path="/upgrade"
                  element={
                    <div className="flex-1">
                      <Upgrade />
                    </div>
                  }
                />
                <Route
                  path="/upgrade-yearly"
                  element={
                    <div className="flex-1">
                      <UpgradeYearly />
                    </div>
                  }
                />
                <Route
                  path="/upgrade-creator"
                  element={
                    <div className="flex-1">
                      <UpgradeCreator />
                    </div>
                  }
                />
                <Route
                  path="/lifetime"
                  element={<PreserveSearchRedirect to="/lt" />}
                />
                <Route path="/lt2/*" element={<LifetimeMay12Routes />} />
                <Route
                  path="/lt"
                  element={<PreserveSearchRedirect to="/lt2" />}
                />
                <Route
                  path="/life-time"
                  element={<PreserveSearchRedirect to="/lt" />}
                />
                <Route
                  path="/lifetime-offer"
                  element={<PreserveSearchRedirect to="/lt" />}
                />
                <Route
                  path="/lifetime-funnel"
                  element={
                    <div className="flex-1">
                      <LifetimeFunnel />
                    </div>
                  }
                />
                <Route
                  path="/lifetime-funnel/checkout"
                  element={
                    <div className="flex-1">
                      <SmallCheckoutNavbar />
                      <LifetimeFunnelCheckoutPage />
                      <SmallFooter />
                    </div>
                  }
                />
                <Route
                  path="/lifetime-funnel/upgrade/credits"
                  element={
                    <div className="flex-1">
                      <SmallCheckoutNavbar />
                      <LifetimeFunnelUpgradePage upgradeKey="upgrade_credits" />
                      <SmallFooter />
                    </div>
                  }
                />
                <Route
                  path="/lifetime-funnel/upgrade/accelerator"
                  element={
                    <div className="flex-1">
                      <SmallCheckoutNavbar />
                      <LifetimeFunnelAcceleratorUpgradePage />
                      <SmallFooter />
                    </div>
                  }
                />
                <Route
                  path="/lifetime-funnel/thank-you"
                  element={
                    <div className="flex-1">
                      <SmallCheckoutNavbar />
                      <LifetimeFunnelThankYouPage />
                      <SmallFooter />
                    </div>
                  }
                />
                <Route
                  path="/author-accelerator"
                  element={
                    <AuthorAcceleratorPage />
                  }
                />
                <Route
                  path="/author-accelerator/video1"
                  element={<AuthorAcceleratorVideoPage slug="video1" />}
                />
                <Route
                  path="/author-accelerator/video2"
                  element={<AuthorAcceleratorVideoPage slug="video2" />}
                />
                <Route
                  path="/author-accelerator/video3"
                  element={<AuthorAcceleratorVideoPage slug="video3" />}
                />
                <Route
                  path="/starter-kit"
                  element={<Navigate to="/lifetime" replace />}
                />
                <Route
                  path="/lifetime-deal"
                  element={<PreserveSearchRedirect to="/lt" />}
                />
                <Route
                  path="/funnel"
                  element={
                    <div className="flex-1">
                      <FunnelPage />
                    </div>
                  }
                />
                {/* start of the post lifetime purchase funnel*/}
                <Route
                  path="/upgrade-offer"
                  element={<PreserveSearchRedirect to="/growth-autopilot-offer" />}
                />
                <Route
                  path="/upgrade-offer-new"
                  element={<PreserveSearchRedirect to="/growth-autopilot-offer" />}
                />
                <Route
                  path="/growth-autopilot-offer"
                  element={<GrowthAutopilotOffer />}
                />
                <Route
                  path="/credits-offer-new"
                  element={<CreditsOfferPeakJanuary2026 />}
                />
                <Route
                  path="/credits-offer"
                  element={<PreserveSearchRedirect to="/credits-offer-new" />}
                />
                <Route path="/success" element={<Success />} />
                <Route path="/lifetime-upgrade" element={<LifetimeUpgrade />} />
                <Route path="/orderbump-success" element={<OrderbumpSuccess />} />
                {/* end of the post lifetime purchase funnel*/}
                <Route
                  path="/subscription_success"
                  element={<SubscriptionSuccess />}
                />
                <Route
                  path="/payg_success"
                  element={<PAYGSuccess />}
                />

                <Route path="/add-credits" element={<AddCredits />} />
                <Route path="/black-friday" element={<AddCreditsOfferEnded />} />
                <Route path="/christmas" element={<AddCreditsOfferEnded />} />
                <Route path="/happy-new-year" element={<AddCreditsOfferEnded />} />
                <Route path="/valentine-deal" element={<AddCreditsOfferEnded />} />
                <Route path="/flash-sale" element={<AddCreditsFlashSale />} />
                <Route path="/easter-special" element={<AddCreditsOfferEnded />} />
                <Route path="/mothers-day" element={<AddCreditsMothersDay />} />

                <Route path="/free" element={<Navigate to="/pricing" replace />} />
                <Route path="/signin" element={<SignIn />} />
                <Route path="/sign-in" element={<Navigate to="/signin" replace />} />
                <Route path="/signup" element={<SignUp />} />

                <Route
                  path="*"
                  element={
                    <>
                      <Navbar />
                      <VisitorLifetimeOffer />
                      <main className="flex-1">
                        <ErrorBoundary name="main-routes" fallback={<RouteErrorFallback />}>
                        <Routes>
                          <Route path="/" element={<Index />} />
                          <Route path="/create" element={<Create />} />
                          <Route path="/book-marketing" element={<BookMarketing />} />
                          <Route path="/book-marketing/:slug" element={<BookMarketing />} />
                          <Route path="/native-alternatives" element={<NativeAlternatives />} />
                          <Route path="/bookstera-vs-native" element={<BooksteraVsNative />} />
                          <Route path="/growth-autopilot" element={<GrowthAutopilot />} />
                          <Route
                            path="/privacy-policy"
                            element={<PrivacyPolicy />}
                          />
                          <Route
                            path="/acceptable-use-policy"
                            element={<AcceptableUsePolicy />}
                          />
                          <Route path="/code-of-conduct" element={<CodeOfConduct />} />
                          <Route path="/terms" element={<Terms />} />
                          <Route path="/imprint" element={<Imprint />} />
                          <Route path="/flexco" element={<Flexco />} />
                          <Route path="/partners" element={<Partners />} />
                          <Route path="/affiliate" element={<AffiliatePage /
                          >} />
                          <Route path="/superscale" element={<Superscale />} />
                          <Route path="/affiliate-challenge" element={<AffiliateChallenge />} />
                          <Route path="/affiliate-challenge/leaderboard" element={<AffiliateChallengeLeaderboard />} />
                          <Route path="/affiliate-challenge/resources" element={<AffiliateChallengeResources />} />
                          <Route
                            path="/affiliate-redirect"
                            element={<AffiliateRedirect />}
                          />

                          <Route path="/pricing" element={<Pricing />} />
                          <Route path="/reviews" element={<Reviews />} />
                          <Route path="/is-bookstera-legit" element={<IsBooksteraLegit />} />
                          <Route path="/about" element={<About />} />
                          <Route path="/success-stories" element={<SuccessStories />} />
                          <Route path="/enterprise/solutions" element={<EnterpriseSolutions />} />


                          <Route path="/coaching" element={<Coaching />} />
                          <Route path="/coaching-thankyou" element={<CoachingThankYou />} />
                          <Route path="/contact" element={<Contact />} />
                          <Route
                            path="/contact/thank-you"
                            element={<ContactThankYou />}
                          />
                          <Route path="/support" element={<Support />} />
                          <Route path="/help" element={<Help />} />
                          <Route path="/bookster" element={<Bookster />} />
                          <Route path="/agency/contact" element={<Navigate to="/enterprise/contact" replace />} />
                          <Route path="/enterprise/contact" element={<EnterpriseContact />} />
                          <Route path="/marketplace/contact" element={<MarketplaceContact />} />
                          <Route path="/press" element={<Press />} />
                          <Route path="/partnerships" element={<Partnerships />} />

                          <Route path="/for-coaches" element={<UseCasePage audience="coaches" />} />
                          <Route path="/for-consultants" element={<UseCasePage audience="consultants" />} />
                          <Route path="/for-course-creators" element={<UseCasePage audience="course-creators" />} />
                          <Route path="/for-agencies" element={<UseCasePage audience="agencies" />} />
                          <Route path="/for-authors" element={<UseCasePage audience="authors" />} />
                          <Route path="/welcome" element={<Welcome />} />
                          <Route path="/reset" element={<ResetPassword />} />

                          <Route
                            path="/profile"
                            element={
                              <AuthGuard>
                                <Profile />
                              </AuthGuard>
                            }
                          />

                          <Route path="/features" element={<Features />} />
                          <Route path="/growth" element={<PreserveSearchRedirect to="/book-growth" />} />
                          <Route path="/book-growth" element={<BookGrowth />} />
                          <Route path="/self-publisher-playbooks" element={<SelfPublisherPlaybooks />} />
                          <Route path="/publish" element={<Navigate to="/create-book" replace />} />
                          <Route
                            path="/create-book/:bookId?/:step?"
                            element={
                              <AuthGuard requireBookOwnership={true}>
                                <CreateBook />
                              </AuthGuard>
                            }
                          />
                          <Route
                            path="/edit-chapter/:bookId/:chapterId"
                            element={
                              <AuthGuard requireBookOwnership={true}>
                                <ChapterEdit />
                              </AuthGuard>
                            }
                          />

                          <Route
                            path="/pseudonyms"
                            element={
                              <AuthGuard>
                                <Pseudonyms />
                              </AuthGuard>
                            }
                          />
                          <Route
                            path="/my-books"
                            element={
                              <AuthGuard>
                                <MyBooks />
                              </AuthGuard>
                            }
                          />
                          <Route
                            path="/author-dashboard"
                            element={
                              <AuthGuard>
                                <AuthorDashboard />
                              </AuthGuard>
                            }
                          />
                          <Route
                            path="/admin-dashboard"
                            element={
                              <AdminGuard>
                                <AdminDashboard />
                              </AdminGuard>
                            }
                          />
                          <Route path="/pseudonyms" element={<Pseudonyms />} />
                          <Route path="/purchases" element={<Purchases />} />
                          <Route path="/bundles" element={<AuthGuard><Bundles /></AuthGuard>} />
                          <Route path="/blog" element={<Blog />} />
                          <Route path="/blog/:slug" element={<BlogPost />} />
                          <Route path="/ai-book-generator" element={<SeoContentPage />} />
                          <Route path="/create-an-ebook-with-ai" element={<SeoContentPage />} />
                          <Route path="/compare" element={<CompareHub />} />
                          <Route path="/compare/:competitorSlug" element={<DedicatedComparisonPage />} />
                          <Route path="/lead-magnet-generator" element={<SeoContentPage />} />
                          <Route path="/amazon-kdp-ai" element={<SeoContentPage />} />
                          <Route path="/tools" element={<ToolsDirectoryPage />} />
                          <Route path="/tools/categories" element={<ToolsCategoriesPage />} />
                          <Route path="/tools/free" element={<ToolsFreePage />} />
                          <Route path="/tools/submit" element={<ToolsSubmitPage />} />
                          <Route path="/tools/methodology" element={<ToolsMethodologyPage />} />
                          <Route path="/tools/category/:categorySlug" element={<ToolCategoryPage />} />
                          <Route path="/tools/compare/:comparisonSlug" element={<ToolComparisonPage />} />
                          <Route path="/tools/:toolSlug" element={<ToolDetailPage />} />
                          <Route path="/profitable-book-idea-generator" element={<BookIdeas />} />
                          <Route path="/book-ideas" element={<Navigate to="/profitable-book-idea-generator" replace />} />
                          <Route path="/book-ideas/:slug" element={<BookIdeasResult />} />
                          <Route path="/mothers-day-book-idea-generator" element={<MothersDayBookIdeaGenerator />} />
                          <Route path="/bookstera-o-matic" element={<BooksteraOMatic />} />
                          <Route path="/survey" element={<Survey />} />
                          <Route path="/free-course" element={<WaitlistPage />} />
                          <Route path="/masterclass" element={<WaitlistPage />} />

                          <Route
                            path="/book-preview/:promptId"
                            element={<BookPreview />}
                          />
                          <Route path="/book-preview" element={<BookPreview />} />
                          <Route path="/marketplace" element={<RedesignedMarketplace />} />
                          <Route path="/marketplace/free" element={<Navigate to="/marketplace?price=free" replace />} />
                          <Route path="/marketplace/latest" element={<Navigate to="/marketplace?sort=new" replace />} />
                          <Route path="/marketplace/featured" element={<Navigate to="/marketplace?sort=toprated" replace />} />
                          {MARKETPLACE_CATEGORY_SLUGS.map(({ slug }) => <Route key={slug} path={`/marketplace/${slug}`} element={<RedesignedMarketplace categorySlug={slug} />} />)}
                          <Route path="/marketplace/:bookId/audiobook" element={<BookDetails />} />
                          <Route path="/marketplace/:bookId" element={<BookDetails />} />
                          <Route path="/marketplace/bundle/:bundleId" element={<MarketplaceBundleDetails />} />
                          <Route path="/authors" element={<AuthorsPage />} />
                          <Route
                            path="/authors/:pseudonymId"
                            element={<AuthorPage />}
                          />
                          <Route path="/features" element={<Features />} />
                          <Route
                            path="/business-models/:slug"
                            element={<BusinessModel />}
                          />

                          <Route path="*" element={<NotFoundPage />} />
                        </Routes>
                        </ErrorBoundary>
                      </main>
                      <Footer />
                    </>
                  }
                />
              </Routes>
              </Suspense>
            </ErrorBoundary>
          </BrowserRouter>
        </div>
      </TooltipProvider>
    </QueryClientProvider>
  );
}
export default App;
